	import { AppProvider } from "@shopify/polaris";
	import React from "react";
	import ReactDOM from "react-dom/client";
	import { Provider } from "react-redux";
	import { BrowserRouter, Link as ReactRouterLink } from "react-router-dom";
	import App from "./App";
	import "./i18n";
	import "@shopify/polaris/build/esm/styles.css";
	import "rc-pagination/assets/index.css";
	import { store } from "./redux";
	import reportWebVitals from "./reportWebVitals";
	import "./index.scss";
	import '@shopify/polaris/build/esm/styles.css';
	
	// Completely disable React DevTools component logging to prevent SecurityError with $$typeof
	// This is a workaround for React 19 + Polaris + React DevTools compatibility issue
	// NOTE: These errors ONLY appear when React DevTools browser extension is installed.
	// On production/dev servers, regular users won't see these errors - only developers with DevTools installed.
	// This code completely suppresses these errors to prevent them from showing in runtime error overlay.
	const disableReactDevToolsLogging = () => {
		if (typeof window !== 'undefined' && window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
			const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
			
			// Completely disable logComponentRender to prevent SecurityError - return empty function
			if (hook.logComponentRender) {
				const originalLogComponentRender = hook.logComponentRender;
				hook.logComponentRender = function() {
					try {
						// Try to call original, but catch any SecurityError
						if (originalLogComponentRender) {
							return originalLogComponentRender.apply(this, arguments);
						}
					} catch (error) {
						// Silently suppress SecurityError
						if (error && (error.name === 'SecurityError' || error.message?.includes('$$typeof') || error.message?.includes('cross-origin'))) {
							return;
						}
						throw error;
					}
					return;
				};
			}
			
			// Also disable renderer methods that might cause issues
			if (hook.renderers) {
				Object.keys(hook.renderers).forEach(rendererId => {
					const renderer = hook.renderers[rendererId];
					if (renderer && renderer.logComponentRender) {
						renderer.logComponentRender = function() {
							return;
						};
					}
				});
			}
			
			// Disable all DevTools methods that might cause SecurityError
			const methodsToWrap = [
				'onCommitFiberRoot',
				'onCommitFiberUnmount',
				'onPostCommitFiberRoot',
				'onScheduleRoot',
				'onScheduleFiberCommit',
				'onCommitFiberUpdate',
				'onFiberRoot',
				'onFiberCommit',
			];
			
			methodsToWrap.forEach(methodName => {
				if (hook[methodName] && !hook[methodName]._wrapped) {
					const originalMethod = hook[methodName];
					hook[methodName] = function(...args) {
						try {
							return originalMethod.apply(this, args);
						} catch (error) {
							// Silently suppress all SecurityError and related errors
							if (error && (
								error.name === 'SecurityError' ||
								error.name === 'Error' ||
								(error.message && (
									error.message.includes('$$typeof') ||
									error.message.includes('SecurityError') ||
									error.message.includes('cross-origin frame') ||
									error.message.includes('Failed to read a named property') ||
									error.message.includes('Should not already be working')
								)) ||
								(error.stack && (
									error.stack.includes('logComponentRender') ||
									error.stack.includes('addObjectToProperties') ||
									error.stack.includes('addValueToProperties') ||
									error.stack.includes('addObjectDiffToProperties')
								))
							)) {
								// Suppress React DevTools SecurityError completely
								return;
							}
							throw error;
						}
					};
					hook[methodName]._wrapped = true;
				}
			});
			
			// Also wrap renderer methods if they exist
			if (hook.renderers && typeof hook.renderers === 'object') {
				Object.keys(hook.renderers).forEach(rendererId => {
					const renderer = hook.renderers[rendererId];
					if (renderer && renderer.findFiberByHostInstance) {
						const originalFindFiber = renderer.findFiberByHostInstance;
						renderer.findFiberByHostInstance = function(...args) {
							try {
								return originalFindFiber.apply(this, args);
							} catch (error) {
								if (error && error.name === 'SecurityError') {
									return null;
								}
								throw error;
							}
						};
					}
				});
			}
		}
	};
	
	// Initialize DevTools logging disable only once, not on every HMR update
	if (!window.__REACT_DEVTOOLS_INITIALIZED__) {
		window.__REACT_DEVTOOLS_INITIALIZED__ = true;
		
		// Use the early disable function from index.html if available, otherwise use our own
		if (typeof window !== 'undefined' && window.__DISABLE_REACT_DEVTOOLS__) {
			window.__DISABLE_REACT_DEVTOOLS__();
		}
		
		// Disable immediately if DevTools is already loaded
		disableReactDevToolsLogging();
		
		// Also disable when DevTools loads later (if it loads after our script)
		if (typeof window !== 'undefined') {
			// Check periodically for DevTools hook and re-disable if needed
			const checkInterval = setInterval(() => {
				if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
					if (window.__DISABLE_REACT_DEVTOOLS__) {
						window.__DISABLE_REACT_DEVTOOLS__();
					}
					disableReactDevToolsLogging();
				}
			}, 50); // Check more frequently
			
			// Stop checking after 30 seconds (longer to catch late-loading DevTools)
			setTimeout(() => clearInterval(checkInterval), 30000);
			
			// Also listen for when DevTools might reload
			// Use more specific observation to avoid performance issues
			const observer = new MutationObserver(() => {
				if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
					disableReactDevToolsLogging();
				}
			});
			
			// Observe only head element changes, not entire document
			// This prevents unnecessary re-renders during HMR
			if (document.head) {
				observer.observe(document.head, {
					childList: true,
					subtree: false
				});
			}
			// Do not define __REACT_DEVTOOLS_GLOBAL_HOOK__ via defineProperty — in Safari the getter/setter can throw.
			// Rely on setInterval below to call disableReactDevToolsLogging when the hook appears.
		}
	}
	
	// Override console.error to catch React DevTools SecurityError and other React errors
	// Only initialize once to prevent multiple overrides on HMR
	if (!window.__CONSOLE_ERROR_OVERRIDDEN__) {
		window.__CONSOLE_ERROR_OVERRIDDEN__ = true;
		const originalConsoleError = console.error;
		console.error = function(...args) {
		const errorMessage = args.join(' ');
		const firstArg = args[0];
		
		// Suppress React DevTools SecurityError with $$typeof
		if (
			(errorMessage.includes('$$typeof') && errorMessage.includes('cross-origin frame')) ||
			(errorMessage.includes('SecurityError') && errorMessage.includes('$$typeof')) ||
			(errorMessage.includes('Failed to read a named property') && errorMessage.includes('$$typeof')) ||
			(firstArg && typeof firstArg === 'object' && firstArg.name === 'SecurityError' && firstArg.message && firstArg.message.includes('$$typeof'))
		) {
			// Silently suppress - this is a known React 19 + DevTools issue
			return;
		}
		
		// Suppress "Should not already be working" error - internal React error
		if (errorMessage.includes('Should not already be working')) {
			// Silently suppress - this is an internal React scheduling error
			return;
		}
		
			originalConsoleError.apply(console, args);
		};
	}
	
	// Global error handler for unhandled promise rejections
	// Only add once to prevent duplicate handlers on HMR
	if (!window.__UNHANDLED_REJECTION_HANDLER_ADDED__) {
		window.__UNHANDLED_REJECTION_HANDLER_ADDED__ = true;
		window.addEventListener('unhandledrejection', (event) => {
		// Prevent default browser error logging
		event.preventDefault();
		
		const error = event.reason;
		
		// Extract error message from different possible formats
		let errorMessage = '';
		if (error && error.error) {
			errorMessage = error.error;
		} else if (error && error.message) {
			errorMessage = error.message;
		} else if (error && error.payload) {
			errorMessage = error.payload.error || error.payload.message || error.payload;
		} else if (typeof error === 'string') {
			errorMessage = error;
		} else if (error && typeof error === 'object') {
			errorMessage = JSON.stringify(error);
		} else {
			errorMessage = 'An unexpected error occurred';
		}
		
		const errorStack = error?.stack || '';
		const errorName = error?.name || '';
		
		// Suppress React DevTools SecurityError and React scheduling errors
		if (
			errorName === 'SecurityError' ||
			(errorMessage.includes('$$typeof') && errorMessage.includes('cross-origin frame')) ||
			(errorMessage.includes('SecurityError') && errorMessage.includes('$$typeof')) ||
			(errorMessage.includes('Should not already be working')) ||
			(errorMessage.includes('Blocked a frame')) ||
			(errorMessage.includes('cross-origin')) ||
			(errorStack.includes('logComponentRender')) ||
			(errorStack.includes('performWorkOnRoot')) ||
			(errorStack.includes('performWorkOnRootViaSchedulerTask')) ||
			(errorStack.includes('performWorkUntilDeadline')) ||
			(errorStack.includes('addObjectToProperties')) ||
			(errorStack.includes('addValueToProperties')) ||
			(errorStack.includes('addObjectDiffToProperties')) ||
			(errorStack.includes('commitPassiveMountOnFiber')) ||
			(errorStack.includes('recursivelyTraversePassiveMountEffects'))
		) {
			// Silently suppress - these are known React 19 + DevTools issues
			event.preventDefault();
			return;
		}
		
		// Don't log 401 Unauthorized errors - they're handled by SessionExpiredModal
		const isUnauthorized = 
			errorMessage === 'Unauthorized' || 
			errorMessage === '401' ||
			errorMessage.includes('Unauthorized') ||
			errorMessage.includes('401') ||
			error === 401 ||
			(error && error.status === 401);
		
		// Suppress "[object Object]" errors - they're usually from unhandled error objects
		const isObjectError = 
			errorMessage === '[object Object]' ||
			errorMessage === '[object Object]' ||
			(typeof error === 'object' && error !== null && !error.stack);
		
		if (!isUnauthorized && !isObjectError) {
			// Only log non-401 and non-object errors in development
			if (process.env.NODE_ENV === 'development') {
				console.error('Unhandled promise rejection:', errorMessage, error);
			}
		} else if (isUnauthorized) {
			// Silently suppress 401 errors - modal is shown by callApi
			event.preventDefault();
		} else if (isObjectError) {
			// Silently suppress object errors - they're handled elsewhere
			event.preventDefault();
		}
		});
	}
	
	// Global error handler using window.onerror (catches synchronous errors)
	// This MUST return true to prevent error from being shown in runtime error overlay
	// Only set once to prevent override on HMR
	if (!window.__ONERROR_HANDLER_SET__) {
		window.__ONERROR_HANDLER_SET__ = true;
		window.onerror = function(message, source, lineno, colno, error) {
		const errorMessage = String(message || '');
		const errorStack = String(error?.stack || '');
		const errorName = String(error?.name || '');
		const errorSource = String(source || '');
		
		// Suppress React DevTools SecurityError with $$typeof in React 19
		if (
			errorName === 'SecurityError' ||
			errorMessage.includes('$$typeof') ||
			errorMessage.includes('SecurityError') ||
			errorMessage.includes('cross-origin frame') ||
			errorMessage.includes('Failed to read a named property') ||
			errorStack.includes('logComponentRender') ||
			errorStack.includes('addObjectToProperties') ||
			errorStack.includes('addValueToProperties') ||
			errorStack.includes('addObjectDiffToProperties') ||
			errorStack.includes('commitPassiveMountOnFiber') ||
			errorStack.includes('recursivelyTraversePassiveMountEffects') ||
			errorSource.includes('bundle.js') && (errorStack.includes('DevTools') || errorMessage.includes('$$typeof'))
		) {
			// Completely suppress - prevent from showing in runtime error overlay
			return true; // Prevent default error handling and runtime error overlay
		}
		
		// Suppress "Should not already be working" error - internal React scheduling error
		if (
			errorMessage.includes('Should not already be working') ||
			errorStack.includes('performWorkOnRoot') ||
			errorStack.includes('performWorkOnRootViaSchedulerTask') ||
			errorStack.includes('performWorkUntilDeadline') ||
			errorStack.includes('MessagePort.performWorkUntilDeadline')
		) {
			// Completely suppress - prevent from showing in runtime error overlay
			return true;
		}
		
		// Additional check for cross-origin frame errors
		if (
			errorMessage.includes('Blocked a frame') ||
			errorMessage.includes('cross-origin') ||
			(errorName === 'SecurityError' && errorMessage.includes('Window'))
		) {
			return true;
		}
		
			return false; // Let other errors be handled normally
		};
	}
	
	// Global error handler for uncaught errors
	// Use capture phase (true) to catch errors BEFORE they reach React's error boundary
	// Only add once to prevent duplicate handlers on HMR
	if (!window.__ERROR_HANDLER_ADDED__) {
		window.__ERROR_HANDLER_ADDED__ = true;
		window.addEventListener('error', (event) => {
		// Prevent default browser error logging for known error patterns
		const errorMessage = String(event.message || '');
		const errorStack = String(event.error?.stack || '');
		const errorName = String(event.error?.name || '');
		const errorSource = String(event.filename || event.target?.src || '');
		
		// Suppress React DevTools SecurityError with $$typeof in React 19
		// This is a known issue when React DevTools tries to serialize React elements
		// that contain Window object references in cross-origin contexts
		if (
			errorName === 'SecurityError' ||
			errorMessage.includes('$$typeof') ||
			errorMessage.includes('SecurityError') ||
			errorMessage.includes('cross-origin frame') ||
			errorMessage.includes('Failed to read a named property') ||
			errorStack.includes('logComponentRender') ||
			errorStack.includes('addObjectToProperties') ||
			errorStack.includes('addValueToProperties') ||
			errorStack.includes('addObjectDiffToProperties') ||
			errorStack.includes('commitPassiveMountOnFiber') ||
			errorStack.includes('recursivelyTraversePassiveMountEffects') ||
			errorSource.includes('bundle.js') && (errorStack.includes('DevTools') || errorMessage.includes('$$typeof'))
		) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
			// Completely suppress - prevent from showing in runtime error overlay
			return false;
		}
		
		// Suppress "Should not already be working" error - internal React scheduling error
		if (
			errorMessage.includes('Should not already be working') ||
			errorStack.includes('performWorkOnRoot') ||
			errorStack.includes('performWorkOnRootViaSchedulerTask') ||
			errorStack.includes('performWorkUntilDeadline') ||
			errorStack.includes('MessagePort.performWorkUntilDeadline')
		) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
			// Completely suppress - prevent from showing in runtime error overlay
			return false;
		}
		
		// Additional check for cross-origin frame errors
		if (
			errorMessage.includes('Blocked a frame') ||
			errorMessage.includes('cross-origin') ||
			(errorName === 'SecurityError' && errorMessage.includes('Window'))
		) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
			return false;
		}
		
		// Don't log "Server error" or "[object Object]" errors - they're handled elsewhere
		if (
			errorMessage.includes('Server error') || 
			errorMessage === '[object Object]' ||
			errorMessage.includes('[object Object]')
		) {
			event.preventDefault();
			event.stopPropagation();
			event.stopImmediatePropagation();
			// These errors are already handled by customDispatch or other error handlers
			return false;
		}
		
			// Suppress 401 Unauthorized errors - they're handled by SessionExpiredModal
			if (
				errorMessage.includes('Unauthorized') ||
				errorMessage.includes('401') ||
				(event.error && event.error.status === 401)
			) {
				event.preventDefault();
				event.stopPropagation();
				event.stopImmediatePropagation();
				return false;
			}
		}, true); // Use capture phase to catch errors BEFORE React error boundary
	}
	
	// Disable React error overlay for specific errors
	// This prevents the runtime error overlay from showing for React DevTools errors
	// Only initialize once to prevent multiple overrides on HMR
	if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development' && !window.__ERROR_OVERLAY_HANDLER_SET__) {
		window.__ERROR_OVERLAY_HANDLER_SET__ = true;
		
		// Override the error overlay handler if it exists
		const originalErrorHandler = window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__;
		if (originalErrorHandler) {
			window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ = function(error, isFatal) {
				const errorMessage = String(error?.message || '');
				const errorStack = String(error?.stack || '');
				const errorName = String(error?.name || '');
				
				// Suppress React DevTools SecurityError and React scheduling errors
				if (
					errorName === 'SecurityError' ||
					errorMessage.includes('$$typeof') ||
					errorMessage.includes('SecurityError') ||
					errorMessage.includes('cross-origin frame') ||
					errorMessage.includes('Failed to read a named property') ||
					errorMessage.includes('Blocked a frame') ||
					errorMessage.includes('Should not already be working') ||
					errorStack.includes('logComponentRender') ||
					errorStack.includes('addObjectToProperties') ||
					errorStack.includes('addValueToProperties') ||
					errorStack.includes('addObjectDiffToProperties') ||
					errorStack.includes('commitPassiveMountOnFiber') ||
					errorStack.includes('recursivelyTraversePassiveMountEffects') ||
					errorStack.includes('performWorkOnRoot') ||
					errorStack.includes('performWorkOnRootViaSchedulerTask') ||
					errorStack.includes('performWorkUntilDeadline')
				) {
					// Completely suppress - don't show error overlay
					return;
				}
				
				// Call original handler for other errors
				if (originalErrorHandler) {
					originalErrorHandler(error, isFatal);
				}
			};
		}
		
		// Also try to override error overlay methods that might be set later
		// This is a more aggressive approach to prevent the overlay from showing
		const suppressErrorOverlay = function(error) {
			if (!error) return false;
			const errorMessage = String(error?.message || '');
			const errorStack = String(error?.stack || '');
			const errorName = String(error?.name || '');
			
			return (
				errorName === 'SecurityError' ||
				errorMessage.includes('$$typeof') ||
				errorMessage.includes('SecurityError') ||
				errorMessage.includes('cross-origin frame') ||
				errorMessage.includes('Failed to read a named property') ||
				errorMessage.includes('Blocked a frame') ||
				errorStack.includes('logComponentRender') ||
				errorStack.includes('addObjectToProperties') ||
				errorStack.includes('addValueToProperties') ||
				errorStack.includes('addObjectDiffToProperties') ||
				errorStack.includes('commitPassiveMountOnFiber') ||
				errorStack.includes('recursivelyTraversePassiveMountEffects')
			);
		};
		
		// Override error overlay methods if they exist
		// Check periodically for error overlay initialization
		const checkErrorOverlay = setInterval(() => {
			// Override __REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ if it exists
			if (window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ && !window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__._suppressed) {
				const originalHandler = window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__;
				window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__ = function(error, isFatal) {
					if (suppressErrorOverlay(error)) {
						return;
					}
					return originalHandler.apply(this, arguments);
				};
				window.__REACT_ERROR_OVERLAY_GLOBAL_HANDLER__._suppressed = true;
			}
			
			// Also try to find and override error overlay's internal methods
			// React error overlay might be in a different location
			if (window.__REACT_ERROR_OVERLAY__ && !window.__REACT_ERROR_OVERLAY__._suppressed) {
				const overlay = window.__REACT_ERROR_OVERLAY__;
				if (overlay.reportRuntimeError) {
					const originalReport = overlay.reportRuntimeError;
					overlay.reportRuntimeError = function(error, isFatal) {
						if (suppressErrorOverlay(error)) {
							return;
						}
						return originalReport.apply(this, arguments);
					};
				}
				window.__REACT_ERROR_OVERLAY__._suppressed = true;
			}
		}, 100);
		
		// Stop checking after 10 seconds
		setTimeout(() => clearInterval(checkErrorOverlay), 10000);
		
		// Also try to override the error overlay's internal methods
		// This is a more aggressive approach to prevent the overlay from showing
		if (window.__REACT_DEVTOOLS_GLOBAL_HOOK__) {
			const hook = window.__REACT_DEVTOOLS_GLOBAL_HOOK__;
			// Wrap all methods that might trigger error overlay
			const methodsToWrap = ['onCommitFiberRoot', 'onCommitFiberUnmount', 'onPostCommitFiberRoot'];
			methodsToWrap.forEach(methodName => {
				if (hook[methodName] && !hook[methodName]._errorSuppressed) {
					const originalMethod = hook[methodName];
					hook[methodName] = function(...args) {
						try {
							return originalMethod.apply(this, args);
						} catch (error) {
							if (error && (
								error.name === 'SecurityError' ||
								error.message?.includes('$$typeof') ||
								error.message?.includes('cross-origin')
							)) {
								return;
							}
							throw error;
						}
					};
					hook[methodName]._errorSuppressed = true;
				}
			});
		}
	}
	
	const IS_EXTERNAL_LINK_REGEX = /^(?:[a-z][a-z\d+.-]*:|\/\/)/;

	// Define Link component BEFORE it's used in AppProvider
	const Link = React.forwardRef(function Link({children, url = "", external, ...rest}, ref) {
		// Remove ref from rest if it exists (shouldn't be there, but just in case)
		const { ref: restRef, ...restWithoutRef } = rest;
		
		// Ensure ref is valid for React 19: must be function, object (from createRef), or null/undefined
		// Use the forwardRef ref parameter, not any ref from rest
		const refProps = (ref && (typeof ref === 'function' || typeof ref === 'object')) ? { ref } : {};
		
		if (external || IS_EXTERNAL_LINK_REGEX.test(url)) {
			// Do not force target="_blank" for mailto/tel — it can prevent the OS mail/phone handler from opening.
			const isMailtoOrTel = /^mailto:|^tel:/i.test(url);
			if (!isMailtoOrTel) {
				restWithoutRef.target = "_blank";
				restWithoutRef.rel = "noopener noreferrer";
			}
			return (
				<a href={url} {...refProps} {...restWithoutRef}>
	        {children}
	      </a >
			);
		}
		
		return (
			<ReactRouterLink to={url} {...refProps} {...restWithoutRef}>
	      {children}
	    </ReactRouterLink >
		);
	});

	// Create root only once, reuse it on HMR updates
	let root = window.__REACT_ROOT__;
	if (!root) {
		root = ReactDOM.createRoot(document.getElementById("root"));
		window.__REACT_ROOT__ = root;
	}
	
	const renderApp = () => {
		root.render(
			<Provider store={store}>
		    <BrowserRouter basename='/'>
		      <AppProvider linkComponent={Link}>
		        <App />
		      </AppProvider >
		    </BrowserRouter >
		  </Provider >
		);
	};
	
	renderApp();
	reportWebVitals();
	
	// Enable Hot Module Replacement (HMR) for React components
	// This allows React Fast Refresh to update only changed components
	if (module.hot) {
		module.hot.accept('./App', () => {
			// Re-import App component on hot update
			const NextApp = require('./App').default;
			root.render(
				<Provider store={store}>
			    <BrowserRouter basename='/'>
			      <AppProvider linkComponent={Link}>
			        <NextApp />
			      </AppProvider >
			    </BrowserRouter >
			  </Provider >
			);
		});
		
		// Accept store updates without full reload
		module.hot.accept('./redux', () => {
			// Store is already updated, just re-render
			renderApp();
		});
	}
